import React, { useEffect, useRef, useState } from "react";
import { IoPlayCircle, IoChevronBack, IoChevronForward } from "react-icons/io5";
import { Link } from "react-router-dom";
import Smallbutton from "../../Buttons/Smallbutton";
import HighlightsMain from "./HighlightsMain";
import HighlightsMobileCarosel from "./HighlightsMobileCarosel";
import HighlightsLandingSkeleton from "../Skeleton/Highlights/HighlightsLandingSkeleton";
import { higlightsbanner } from "../../Constants/Data";

function Highlights() {
  const [highlightsData, setHighlightsData] = useState([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [countText, setCountText] = useState("1 of 0");

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHighlightId, setSelectedHighlightId] = useState(null);

  // ✅ should point to the REAL scroll container inside HighlightsMain
  const scrollRef = useRef(null);

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    fetchHighlightsData();
  }, []);

  const fetchHighlightsData = () => {
    fetch(`${process.env.REACT_APP_API_URL_HIGHLIGHTS}`)
      .then((response) => {
        if (!response.ok) throw new Error("Network response was not ok");
        return response.json();
      })
      .then((data) => {
        const list = data?.data || [];
        setHighlightsData(list);

        const total = list.length;
        setCountText(total ? `1 of ${total}` : "1 of 0");
        setCurrentItemIndex(0);

        // ✅ after data load, scroll to first item
        setTimeout(() => {
          scrollToIndex(0);
        }, 0);
      })
      .catch((error) => {
        console.error("Fetch error:", error);
      });
  };

  const updateCountNumber = (index) => {
    const currentCount = index + 1;
    const totalCount = highlightsData.length;
    setCountText(`${currentCount} of ${totalCount}`);
  };

  const scrollToIndex = (index) => {
    const container = scrollRef.current;
    if (!container) return;

    const items = container.querySelectorAll("[data-highlight-item='true']");
    if (!items?.length || !items[index]) return;

    items[index].scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  const handleHighlightClick = (highlightId) => {
    setSelectedHighlightId(highlightId);
    setModalOpen(true);

    const index = highlightsData.findIndex((item) => item.id === highlightId);
    if (index !== -1) {
      setCurrentItemIndex(index);
      updateCountNumber(index);
      scrollToIndex(index);
    }
  };

  const handleClose = () => setModalOpen(false);

  const nextItem = () => {
    if (!highlightsData.length) return;

    const nextIndex =
      currentItemIndex === highlightsData.length - 1 ? 0 : currentItemIndex + 1;

    setCurrentItemIndex(nextIndex);
    updateCountNumber(nextIndex);
    scrollToIndex(nextIndex);
  };

  const prevItem = () => {
    if (!highlightsData.length) return;

    const prevIndex =
      currentItemIndex === 0 ? highlightsData.length - 1 : currentItemIndex - 1;

    setCurrentItemIndex(prevIndex);
    updateCountNumber(prevIndex);
    scrollToIndex(prevIndex);
  };

  if (!highlightsData.length) {
    return (
      <div>
        <HighlightsLandingSkeleton />
      </div>
    );
  }

  return (
    <div
      className="flex md:flex-row flex-col px-[16px] md:px-[24px] pt-[50px] md:pt-0 md:justify-center md:items-end text-start text-white font-Rubik md:h-[616px] md:w-full object-cover"
      style={{
        backgroundImage: `url(${higlightsbanner})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="pb-[110px] md:px-[24px] px-[16px] overflow-hidden">
        <div className="container mx-auto">
          <div className="flex flex-col gap-[16px]">
            {/* Heading */}
            <div className="flex md:justify-between justify-center pr-[30px]">
              <div className="flex text-white italic font-medium gap-[12px] items-center sports-news-heading">
                <IoPlayCircle className="text-[26px]" />
                <h3 className="md:text-[24px] text-[18px]">HIGHLIGHTS</h3>
              </div>

              <Link
                to="/highlightsviewall"
                className="md:block hidden contact-component"
                onClick={handleClick}
              >
                <Smallbutton bgColor="#FFFFFF" textColor="#9E120E">
                  <h2>VIEW ALL</h2>
                </Smallbutton>
              </Link>
            </div>

            {/* DESKTOP */}
            <div className="md:block hidden relative">
              <button
                type="button"
                onClick={prevItem}
                aria-label="Previous"
                className="absolute left-2 top-1/2 -translate-y-1/2
                    bg-white/30 hover:bg-white/50 text-white
                    p-3 rounded-full backdrop-blur-md
                    transition duration-300 z-20"
        >
          <IoChevronBack size={26} />
          </button>

              <HighlightsMain
                scrollRef={scrollRef} // ✅ IMPORTANT
                highlightsData={highlightsData}
                handleHighlightClick={handleHighlightClick}
                modalOpen={modalOpen}
                selectedHighlightId={selectedHighlightId}
                handleClose={handleClose}
                nextItem={nextItem}
                prevItem={prevItem}
                updateCountNumber={updateCountNumber}
                currentItemIndex={currentItemIndex}
                countText={countText}
              />

              <button
                type="button"
                onClick={nextItem}
                aria-label="Next"
                className="absolute right-2 top-1/2 -translate-y-1/2
                          bg-white/30 hover:bg-white/50 text-white
                          p-3 rounded-full backdrop-blur-md
                          transition duration-300 z-20"
              >
                <IoChevronForward size={26} />
              </button>
            </div>

            {/* MOBILE */}
            <div className="md:hidden block">
              <HighlightsMobileCarosel
                highlightsData={highlightsData}
                handleHighlightClick={handleHighlightClick}
                modalOpen={modalOpen}
                selectedHighlightId={selectedHighlightId}
                handleClose={handleClose}
                nextItem={nextItem}
                prevItem={prevItem}
                updateCountNumber={updateCountNumber}
                currentItemIndex={currentItemIndex}
                countText={countText}
              />
            </div>

            <Link
              to="/highlightsviewall"
              className="md:hidden block z-0"
              onClick={handleClick}
            >
              <Smallbutton bgColor="#FFFFFF" textColor="#9E120E">
                <h2>VIEW ALL</h2>
              </Smallbutton>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Highlights;